import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { RefreshCcw } from "lucide-react";

const buttonVariants = cva(
  `inline-flex items-center justify-center whitespace-nowrap BookEZ_btn_radius BookEZ_font_normal transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed hover:cursor-pointer `,
  {
    variants: {
      variant: {
        default: "BookEZ_btn_text_color",
        outline:
          "border BookEZ_btn_outlined_font_color BookEZ_btn_outlined_bg_color BookEZ_btn_outlined_border_color BookEZ_btn_outline",
        outlineBlack:
          "border BookEZ_font_body_medium BookEZ_btn_outlined_font_color BookEZ_btn_outlined_border_color BookEZ_btn_outline_black",
        ghost:
          "BookEZ_font_color_gray_2 border BookEZ_btn_ghost_border BookEZ_btn_ghost",
        filled:
          "BookEZ_btn_filled_bg BookEZ_btn_filled_text_color BookEZ_btn_filled",
        monthPicker:
          "border BookEZ_month_picker_border_color BookEZ_month_picker BookEZ_month_picker_font_color BookEZ_font_light_bold",
        monthPickerDisabled:
          "bg-gray-100 text-gray-400 font-semibold cursor-not-allowed opacity-60",
        underline:
          "bg-transparent BookEZ_btn_underline_blue underline decoration-1 underline-offset-4  hover:decoration-2 transition-all duration-200  focus:decoration-2  disabled:text-gray-400 disabled:no-underline",
        underlineWithoutBorder:
          "bg-transparent BookEZ_btn_underline_blue decoration-1 underline-offset-4  hover:decoration-2 transition-all duration-200  focus:decoration-2  disabled:text-gray-400",
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-6 py-3 px-1",
        sm: "h-8 BookEZ_rounded_xl px-3 BookEZ_font_body_size",
        md: "h-10 BookEZ_rounded_xl px-3 font_body_large",
        lg: "h-10 BookEZ_rounded_xl px-8 font_body_large",
        icon: "h-9 w-9",
        monthPicker: "h-9 px-3 BookEZ_font_body_size ",
        auto: "w-auto h-auto",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        {...props}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={loading || disabled}
      >
        {loading && <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />}
        {props.children}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
